/* eslint-disable react/no-unused-prop-types */
import { DonationProvider } from '@joindeed/calculate-fees';
import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Tooltip,
} from '@mui/material';

import { controlledTextField } from 'components/ReactHookForm/controlledTextField';
import { controlledFileUpload } from 'components/ReactHookForm/controlledFileUpload';
import { selectCategories, selectPillars, selectSDGs } from 'entities/cause/selectors';
import FormDescriptionRteField from 'retired/FormDescriptionRteField';
import PageHeader from 'retired/PageHeader';
import StepperComponent, { Stepper } from 'retired/Stepper';
import Step from 'retired/Stepper/Step';
import StepLabel from 'retired/Stepper/StepLabel';
import { NonprofitsSelector } from 'retired/FormNonprofitsSelector/NonprofitsSelector';
import { imageTypes } from 'retired/FileUpload/utils';
import { ActionVisibilityStatus } from 'generated/graphql';
import { capitalizeFirstLetter } from 'utils/stringFormat';
import { selectUser } from 'entities/user/selectors';
import { maxLengthRichText } from 'utils/validators';

import { MIN_USD_REWARD_AMOUNT, FormFieldValues, ActionFormProps } from './types';
import { DatesStepContent } from './DatesStep';

interface SingleFieldInterface {
  name: string;
  label: string;
  helperText: string;
  validate?: ((value: unknown) => string | undefined)[];
  multiline?: boolean;
  isRichTextInput?: boolean;
  rules?: Record<string, any>;
}
const BasicInformationStepContent = (): JSX.Element => {
  const fields: SingleFieldInterface[] = [
    {
      name: 'title',
      label: 'Title',
      helperText: 'Max. 60 characters',
      rules: {
        maxLength: { value: 60, message: 'Max. 60 characters' },
        required: 'Title is required',
      },
    },
    {
      name: 'subTitle',
      label: 'Subtitle',
      helperText: 'Max. 90 characters',
      rules: {
        maxLength: { value: 90, message: 'Max. 90 characters' },
        required: 'Subtitle is required',
      },
    },
    {
      name: 'description',
      label: 'Description *',
      helperText: 'Max. 5000 characters',
      isRichTextInput: true,
      rules: {
        validate: maxLengthRichText(5000),
        required: 'Description is required',
      },
    },
  ];

  return (
    <Stack spacing={3}>
      {fields.map((f: SingleFieldInterface) => (
        <Controller
          key={f.name}
          name={f.name}
          render={({ field: { ref, ...field }, fieldState }): JSX.Element =>
            f.isRichTextInput ? (
              <FormDescriptionRteField
                input={field}
                label={f.label}
                helperText={f.helperText}
                meta={{
                  touched: fieldState.isTouched || fieldState.isDirty,
                  error: fieldState.error?.message,
                }}
                height={200}
              />
            ) : (
              <TextField
                label={f.label}
                helperText={fieldState.error?.message || f.helperText}
                inputRef={ref}
                fullWidth
                required={!!f.rules?.required}
                error={!!fieldState.error}
                {...field}
              />
            )
          }
          rules={f.rules}
        />
      ))}
    </Stack>
  );
};

interface CauseField {
  name: string;
  label: string;
  maxItems: number;
  items: any;
}
const CausesStepContent = () => {
  const SDGs = useSelector(selectSDGs);
  const categories = useSelector(selectCategories);
  const pillars = useSelector(selectPillars);

  const causeFields: CauseField[] = [
    {
      name: 'categories',
      label: 'Categories & Pillars',
      items: categories.concat(pillars),
      maxItems: 3,
    },
  ];

  if (SDGs.size > 0) {
    causeFields.push({
      name: 'sdgs',
      label: 'SDGs',
      items: SDGs,
      maxItems: 3,
    });
  }

  return (
    <Grid container direction="row" spacing={1}>
      {causeFields.map(({ name, label, items, maxItems }) => (
        <Grid key={`${name}-grid`} item xs={12}>
          <Controller
            key={label}
            name={name}
            render={({ field: { ref, ...field } }) => (
              <FormControl style={{ minWidth: 200 }}>
                <InputLabel id={`${label}-label`}>{label}</InputLabel>
                <Select
                  labelId={`${label}-label`}
                  multiple
                  value={field.value ?? []}
                  onChange={field.onChange}
                  input={<OutlinedInput label={label} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {(selected as string[]).map((value: string) => {
                        const item = items.find((i) => i?.id === value);
                        return item ? <Chip key={`selected-${value}`} label={item.name} /> : null;
                      })}
                    </Box>
                  )}
                >
                  {items.map(
                    (item: any) =>
                      !!item && (
                        <MenuItem
                          key={item.id}
                          value={item.id}
                          disabled={
                            (field.value ?? []).length === maxItems &&
                            !field.value.find((v: string) => v === item.id)
                          }
                        >
                          {item?.name}
                        </MenuItem>
                      )
                  )}
                </Select>
                <FormHelperText>You can select up to three {label}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const SelectedNonprofit = ({ nonprofit }: { nonprofit: { id: string; name: string } }) => (
  <>
    <p style={{ marginBottom: 0 }}>
      <strong>Selected nonprofit: </strong>
      <a href={`/organizations/${nonprofit.id}`} target="_blank">
        {nonprofit?.name}
      </a>
    </p>
    <FormHelperText>
      The reward information can&apos;t be updated after users already interacted with the Action.
    </FormHelperText>
  </>
);
const RewardStepContent = (props: ActionFormProps) => {
  const { actionHasActivities: isDisabled, treeNonprofit } = props;
  const { rewardUnit, rewardAmount, status, nonprofit } = props.formMethods?.getValues() || {};
  const user = useSelector(selectUser);
  const actionDonationRewardRequired = props.showSelectNonprofit
    ? user?.organization?.settings?.get('actionDonationRewardRequired') ?? true
    : false;

  const getIsUSD = (value: string) => value === 'USD';

  const [isNonprofitRequired, setIsNonprofitRequired] = useState(
    getIsUSD(rewardUnit) && actionDonationRewardRequired && status === 'published'
  );
  const [rules, setRules] = useState({
    isUSD: getIsUSD(rewardUnit),
    min: getIsUSD(rewardUnit) ? MIN_USD_REWARD_AMOUNT : 1,
    error: false,
  });
  const { isUSD, min, error } = rules;

  const updateRules = (newValue: string) => {
    let minimumAmount = 0;

    if (actionDonationRewardRequired) {
      minimumAmount = getIsUSD(newValue) ? MIN_USD_REWARD_AMOUNT : 1;
    }
    const isError = rewardAmount < minimumAmount;

    if (newValue === 'tree' && treeNonprofit) {
      props.formMethods.setValue('nonprofit', treeNonprofit);
      props.formMethods.setValue('nonprofitId', treeNonprofit.id);
    }
    setIsNonprofitRequired(
      getIsUSD(newValue) && actionDonationRewardRequired && status === 'published'
    );
    setRules({ isUSD: getIsUSD(newValue), min: minimumAmount, error: isError });
  };

  useEffect(() => {
    updateRules(rewardUnit);
  }, [rewardUnit, rewardAmount, status]);

  const RewardAmountInput = controlledTextField<FormFieldValues>()('rewardAmount', {
    error,
    rules: {
      min,
      required: isNonprofitRequired,
      validate: { min: (value) => Number(value) >= min },
    },
  });
  return (
    <>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={2}>
          <RewardAmountInput
            label="Reward Amount"
            type="number"
            inputMode="numeric"
            error={error}
            disabled={isDisabled}
          />
          {isUSD && actionDonationRewardRequired ? (
            <FormHelperText error={error}>
              Minimum amount for {rewardUnit} is {min}
            </FormHelperText>
          ) : null}
        </Grid>
        <Grid item xs={2}>
          <Controller
            name="rewardUnit"
            render={({ field }) => (
              <FormControl>
                <TextField
                  label="Reward Unit"
                  select
                  required
                  disabled={isDisabled}
                  style={{ width: 200 }}
                  value={field.value}
                  error={error}
                  onChange={(e) => {
                    updateRules(e.target.value);
                    field.onChange(e);
                  }}
                >
                  {['USD', 'tree'].map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {capitalizeFirstLetter(unit)}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            )}
          />
        </Grid>
      </Grid>
      {props.showSelectNonprofit && (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12}>
            {isDisabled && nonprofit ? (
              <SelectedNonprofit nonprofit={nonprofit} />
            ) : rewardUnit === 'tree' && treeNonprofit ? (
              <SelectedNonprofit nonprofit={treeNonprofit} />
            ) : (
              <div style={{ marginTop: 20 }}>
                <Controller<FormFieldValues, 'nonprofit'>
                  name="nonprofit"
                  rules={{ required: isNonprofitRequired }}
                  render={({ field }): JSX.Element => (
                    <NonprofitsSelector
                      required={isNonprofitRequired}
                      limit={1}
                      donationProvider={DonationProvider.PayPal}
                      value={field.value ? [field.value] : []}
                      onChange={([newValue]): void =>
                        field.onChange(
                          newValue?.id ? { id: newValue.id, name: newValue.name } : null
                        )
                      }
                    />
                  )}
                />
              </div>
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};

const ImpactAmountInput = controlledTextField<FormFieldValues>()('impactAmount', {
  rules: {
    required: false,
    default: 0,
    min: 0,
  },
});
const ImpactUnitInput = controlledTextField<FormFieldValues>()('impactUnit', {
  rules: { required: false, default: 'co2' },
});
const ImpactStepContent = (props: ActionFormProps) => {
  const isDisabled = props.actionHasActivities;

  return (
    <>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={2}>
          <ImpactAmountInput
            label="Impact Amount"
            type="number"
            inputMode="numeric"
            disabled={isDisabled}
          />
        </Grid>
        <Grid item xs={2}>
          <ImpactUnitInput label="Impact Unit" select style={{ width: 200 }} disabled={isDisabled}>
            <MenuItem value="co2">kg CO₂</MenuItem>
          </ImpactUnitInput>
        </Grid>
      </Grid>
      <FormHelperText>
        The impact information can&apos;t be updated after users already interacted with the Action.
      </FormHelperText>
    </>
  );
};

const ImageStepContent = (props: ActionFormProps): JSX.Element => {
  const { status } = props?.formMethods?.getValues() || {};

  const [isRequired, setIsRequired] = useState(status === 'published');
  const InputBannerImage = controlledFileUpload<FormFieldValues>()('bannerImage', {
    rules: { required: isRequired },
  });

  useEffect(() => {
    setIsRequired(status === 'published');
  }, [status]);

  return (
    <InputBannerImage
      acceptedTypes={imageTypes}
      label="Action Image (1500x750)"
      imagePreview
      helperText="An image that represents well what your Action is about."
    />
  );
};

const StatusInput = controlledTextField<FormFieldValues>()('status', {
  rules: { required: true, default: 'draft' },
});
const StatusStepContent = (props: ActionFormProps) => {
  const isValid = props.formMethods?.formState?.isValid;
  props.formMethods.watch('status');

  return (
    <div>
      <p style={{ marginTop: 0 }}>
        {!isValid ? (
          <span style={{ color: 'red' }}>
            The {props.title} can be set to &quot;Published&quot; only when all mandatory sections
            have been filled correctly. For now, your {props.title} will be saved in drafts.
          </span>
        ) : (
          <>
            Take some time to review the information you entered. After the {props.title} is
            published and an activity has happened on the {props.title}, it will no longer be
            possible to edit the reward, and you will be liable for any rewards earned by your
            employees.
          </>
        )}
      </p>
      <StatusInput label="Publishing status" select style={{ width: 200, marginTop: 20 }}>
        {Object.values(ActionVisibilityStatus)
          // legacy status was used to deprecate values in the db
          .filter((status) => status !== ActionVisibilityStatus.Legacy)
          .map((status) => (
            <MenuItem key={status} value={status} disabled={!isValid && status === 'published'}>
              {capitalizeFirstLetter(status)}
            </MenuItem>
          ))}
      </StatusInput>
    </div>
  );
};

const VisibilityStepContent = () => {
  const visibilityOptions = [
    {
      key: 'companyOnly',
      label: 'Company Only',
      value: false,
      tip: 'Template only for users in your company',
    },
    {
      key: 'public',
      label: 'Public',
      value: true,
      tip: 'Template can be used to create actions by other companies',
    },
  ];

  return (
    <div>
      <Controller
        name="public"
        render={({ field }) => (
          <RadioGroup
            row
            {...field}
            value={field.value !== undefined ? field.value.toString() : ''} // Ensure the value is controlled and matches the radio button value type
            onChange={(e) => field.onChange(e.target.value === 'true')} // Convert string to boolean on change
          >
            {visibilityOptions.map((option) => (
              <Tooltip title={option.tip} key={option.key}>
                <FormControlLabel
                  value={option.value.toString()} // Convert boolean to string for compatibility with radio buttons
                  control={<Radio />}
                  label={option.label}
                />
              </Tooltip>
            ))}
          </RadioGroup>
        )}
      />
    </div>
  );
};

const getSteps = (
  props: ActionFormProps
): { label: string; id: string; finalStep?: boolean; jsx: JSX.Element }[] => [
  {
    label: 'Basic Information',
    id: 'information',
    jsx: BasicInformationStepContent(),
  },
  {
    label: 'Image',
    id: 'bannerImage',
    jsx: ImageStepContent(props),
  },
  {
    label: 'Reward',
    id: 'reward',
    jsx: RewardStepContent(props),
  },
  {
    label: 'Impact (optional)',
    id: 'impact',
    jsx: ImpactStepContent(props),
  },
  ...(props.showDates
    ? [
        {
          label: 'Start & end dates (optional)',
          id: 'dates',
          jsx: DatesStepContent(props),
        },
      ]
    : []),
  {
    label: `Related Cause Areas${props.showSDGs ? ' and SDGs' : ''} (optional)`,
    id: 'causes',
    jsx: CausesStepContent(),
  },
  ...(props.showSelectVisibility
    ? [
        {
          label: `Visibility`,
          id: 'public',
          jsx: VisibilityStepContent(),
        },
      ]
    : []),
  {
    label: 'Review',
    id: 'status',
    finalStep: true,
    jsx: StatusStepContent(props),
  },
];

const ActionForm: React.FC<ActionFormProps> = (props): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const steps = getSteps(props);

  const onArrowClick = (): void => {
    history.push(props.backLink);
  };

  const canSubmit = !props.isLoading && props.formMethods?.formState?.isValid;
  const { isLoading } = props;
  return (
    <form onSubmit={props.handleSubmit} style={{ maxWidth: 1200 }}>
      <PageHeader
        title={id ? `Edit ${props.title}` : `Create ${props.title}`}
        onArrowClick={onArrowClick}
        buttonText={id ? 'Save Changes' : 'Submit'}
        buttonType="submit"
        buttonDisabled={!canSubmit}
        isLoading={isLoading}
      />

      <Stepper orientation="vertical">
        {steps.map((item, index: number) => (
          <StepperComponent
            key={item.id}
            labelText={item.label}
            componentIndex={index}
            finalStep={item.finalStep}
          >
            {item.jsx}
          </StepperComponent>
        ))}

        {props.showCampaignTip && (
          <Step active completed={false}>
            <StepLabel icon={steps.length + 1}>
              <p>
                If you would like to include this Action in a Campaign, you can do so via the{' '}
                <a href="/campaigns" target="_blank">
                  Campaign overview.
                </a>
              </p>
            </StepLabel>
          </Step>
        )}

        <PageHeader
          onArrowClick={onArrowClick}
          buttonText={id ? 'Save Changes' : 'Submit'}
          buttonType="submit"
          buttonDisabled={!canSubmit}
          isLoading={isLoading}
        />
      </Stepper>
    </form>
  );
};

export default ActionForm;
